import { loadScript } from '@buzzfeed/bf-utils/lib/load-script';
import { getUserId } from '@buzzfeed/bf-utils/lib/userid';
import consent from '@buzzfeed/bf-utils/lib/consent';
const { framework, rules } = consent;
import { PERMUTIVE_TRACKER_NAME } from '../../constants';

const getPermutiveScriptSource = projectId => `https://cdn.permutive.com/${projectId}-web.js`;

const decodeEntities = obj => {
  switch (typeof obj) {
  case 'string': {
    const txt = document.createElement('textarea');
    txt.innerHTML = obj;
    return txt.value;
  }
  case 'object': {
    let empty = true;
    const stripped = Array.isArray(obj) ? [] : {};
    for (let key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        empty = false;
        stripped[key] = decodeEntities(obj[key]);
      }
    }
    return empty ? undefined : stripped;
  }
  }
  return obj;
};

const permutiveFns = [
  'addon',
  'identify',
  'track',
  'trigger',
  'query',
  'segment',
  'segments',
  'ready',
  'on',
  'once',
  'user',
  'consent',
];

export class PermutiveTracker {

  constructor({ cluster, projectId, apiKey }) {
    this.projectId = projectId;
    this.apiKey = apiKey;
    this.cluster = cluster;

    if (typeof window === 'undefined') {
      return {
        trackPageView: () => { },
        trackAffiliateClick: () => { },
        _loadPermutiveScript: () => { },
        _getPermutiveTracker: () => { },
        _mockPermutiveTracker: () => { }
      };
    }

    // if we are not in prod, don't actually call the script - load as a mock logger instead
    // This will not log LinkClick or other events set up by the permutive script itself
    if (cluster !== 'prod') {
      window[PERMUTIVE_TRACKER_NAME] = this._mockPermutiveTracker();
    }
  }

  // Load the actual permutive tracking script an initialize it
  // This should only be called after we have checked user consent
  async _loadPermutiveScript() {

    try {
      /* eslint-disable */
      // https://developer.permutive.com/page/the-permutive-javascript-sdk
      await (async function (n, e, o, r, i) {
        if (!e) {
          (e = e || {}),
            (window[PERMUTIVE_TRACKER_NAME] = e),
            (e.q = []),
            (e.config = i || {}),
            (e.config.projectId = o),
            (e.config.apiKey = r),
            (e.config.environment = e.config.environment || 'production');
          for (var t = [...permutiveFns], c = 0; c < t.length; c++) {
            var f = t[c];
            e[f] = (function (n) {
              return function () {
                var o = Array.prototype.slice.call(arguments, 0);
                e.q.push({
                  functionName: n,
                  arguments: o,
                });
              };
            })(f);
          }
        }
        /* eslint-enable */

        // load permutive script
        await loadScript(getPermutiveScriptSource(o));
      })(document, window[PERMUTIVE_TRACKER_NAME], this.projectId, this.apiKey, {})();

      // add user info tracking
      const userId = await getUserId({ env: this.cluster, legacy: false });
      window[PERMUTIVE_TRACKER_NAME].identify(userId);
      return true;
    } catch {
      return false;
    }
  }

  async _getPermutiveTracker() {

    // skip if we've already loaded / verified consent
    if (window[PERMUTIVE_TRACKER_NAME] && window[PERMUTIVE_TRACKER_NAME].config) {
      return window[PERMUTIVE_TRACKER_NAME];
    }

    // check to see if we have consent before we load anything
    const needsConsent = framework.needsConsent();
    let hasPermutiveConsent;

    if (!needsConsent) {
      await this._loadPermutiveScript();
    } else {
      try {
        hasPermutiveConsent = await rules.hasConsented('permutive');
        if (hasPermutiveConsent) {
          await this._loadPermutiveScript();
        }
      } catch {
        // @todo - sentry log error?
        // permutive failed to load or timed out, set mock fns below to prevent errors
        // we won't be collecting events in this case
      }
    }

    // if we've made it this far, we don't have consent or couldn't load the script
    // load mock fns to prevent app error, but don't log anything out
    if (!window[PERMUTIVE_TRACKER_NAME]) {

      window[PERMUTIVE_TRACKER_NAME] = this._mockPermutiveTracker();
      delete window[PERMUTIVE_TRACKER_NAME].config;

      // if we explicitly do not have consent, mock config so we don't try to load again for this page
      // otherwise, we will retry load the next time this function is called
      if (typeof hasPermutiveConsent !== 'undefined' && !hasPermutiveConsent) {
        window[PERMUTIVE_TRACKER_NAME].config = {};
      }
    }

    return window[PERMUTIVE_TRACKER_NAME];
  }

  async logPermutiveEvent(eventName, eventData) {
    let hasPermutiveConsent = await rules.hasConsented('permutive');
    if (hasPermutiveConsent) {
      console.group('Permutive Tracking Event');
      console.log({ eventName });
      console.log({ eventData });
      console.groupEnd();
    }
  }

  _mockPermutiveTracker() {

    let tracker = {};
    for (let f of permutiveFns) {
      tracker[f] = this.cluster === 'prod' ? () => {} : this.logPermutiveEvent;
    }
    tracker.segments = [];
    tracker.config = {};
    return tracker;
  }

  async trackEvent(eventName, eventData) {
    const tracker = await this._getPermutiveTracker();
    tracker.track(eventName, decodeEntities(eventData));
  }

  async trackPageView(eventData) {
    const tracker = await this._getPermutiveTracker();
    tracker.addon('web', decodeEntities(eventData));
  }

  trackAffiliateClick(eventData) {
    this.trackEvent('AffiliateLinkClick', eventData);
  }

}
